// Backend API Data Models & Entities based on the API Integration Guide

// Common Types
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ErrorResponse {
  message: string;
  error?: string;
  traceId?: string;
}

// Authentication Types
export interface LoginDto {
  email: string;
  password: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  fullName: string;
  restaurantId?: number;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  token?: string;
  user?: {
    id: string;
    email: string;
    roles: string[];
  };
}

// Customer Types
export interface CustomerDto {
  customerId: string; // Guid
  restaurantId: number;
  fullName: string;
  phoneNumber: string;
  email?: string;
  registrationDate: Date;
  currentPointBalance: number;
  totalPointsEarned: number;
  lastVisitDate?: Date;
  // New loyalty fields
  tier?: string;
  totalSpent?: number;
  visitCount?: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt?: Date;
}

export interface CustomerDetailDto extends CustomerDto {
  dateOfBirth?: Date;
  recentTransactions: TransactionDto[];
  recentRedemptions: RewardRedemptionDto[];
}

export interface CreateCustomerDto {
  restaurantId: number;
  fullName: string;
  phoneNumber: string;
  email?: string;
  dateOfBirth?: Date;
}

export interface UpdateCustomerDto {
  fullName: string;
  phoneNumber: string;
  email?: string;
  dateOfBirth?: Date;
  isActive: boolean;
}

// Loyalty Program Types
export interface LoyaltyProgramDetailDto {
  programId: string; // Guid
  restaurantId: number;
  programName: string;
  pointsPerRupee: number;
  // New configurable bonus/extra point fields
  signupBonus?: number;
  birthdayPoints?: number;
  reviewPoints?: number;
  minimumRedemptionPoints?: number;
  referralPointsReferrer?: number;
  referralPointsReferee?: number;
  // Redemption mapping
  equivalentLoyaltyPoints?: number;
  rupeesDiscountValue?: number;
  minimumOrderAmount: number;
  pointExpiryDays: number;
  isActive: boolean;
  isConfigured?: boolean;
  createdAt: Date;
  updatedAt?: Date;
  rewardCatalog: RewardDto[];
}

export interface CreateLoyaltyProgramDto {
  restaurantId: number;
  programName: string;
  pointsPerRupee: number;
  // Optional extra fields
  signupBonus?: number;
  birthdayPoints?: number;
  reviewPoints?: number;
  minimumRedemptionPoints?: number;
  referralPointsReferrer?: number;
  referralPointsReferee?: number;
  // Redemption mapping
  equivalentLoyaltyPoints?: number;
  rupeesDiscountValue?: number;
  minimumOrderAmount: number;
  pointExpiryDays: number; // Default: 365
  isActive: boolean; // Default: true
}

export interface UpdateLoyaltyProgramDto {
  programName: string;
  pointsPerRupee: number;
  // Optional extra fields
  signupBonus?: number;
  birthdayPoints?: number;
  reviewPoints?: number;
  minimumRedemptionPoints?: number;
  referralPointsReferrer?: number;
  referralPointsReferee?: number;
  // Redemption mapping
  equivalentLoyaltyPoints?: number;
  rupeesDiscountValue?: number;
  minimumOrderAmount: number;
  pointExpiryDays: number;
  isActive: boolean;
}

export interface LoyaltyTierDto {
  tierId: string; // Guid
  restaurantId: number;
  tierName: string;
  minimumPoints: number;
  pointsMultiplier: number;
  colorHex?: string;
  description?: string;
}

// Transaction Types
export enum TransactionType {
  EARNED = 1,
  REDEEMED = 2,
  EXPIRED = 3,
  MANUAL_ADJUSTMENT = 4,
}

export interface TransactionDto {
  transactionId: string; // Guid
  customerId: string; // Guid
  customerName?: string; // Made optional as it's missing in some responses
  restaurantId: number;
  transactionType: TransactionType;
  transactionTypeName?: string; // New field from API
  orderAmount?: number;
  orderId?: number; // Order ID this transaction is linked to
  pointsChanged: number;
  pointsBalance?: number; // New field from API
  pointsBalanceBefore?: number; // Made optional
  pointsBalanceAfter?: number; // Made optional
  transactionDate: Date;
  expiryDate?: Date;
  description?: string;
  createdBy?: string;
  createdAt?: Date; // Made optional
}

export interface TransactionDetailDto extends TransactionDto {
  canReverse: boolean;
}

export interface AwardPointsDto {
  customerId: string; // Guid
  restaurantId: number;
  orderAmount: number;
  description?: string;
  createdBy?: string;
}

export interface RedeemPointsDto {
  customerId: string; // Guid
  rewardId: string; // Guid
  orderId: number; // Required: Order ID this reward is linked to
  description?: string;
  createdBy?: string;
}

export interface ManualAdjustmentDto {
  customerId: string; // Guid
  restaurantId: number;
  pointsChanged: number; // Can be positive or negative
  description: string;
  createdBy: string;
}

// Reward Types
export enum RewardType {
  DISCOUNT = 1,
  FREE_ITEM = 2,
  CASHBACK = 3,
}

export interface RewardDto {
  rewardId: string; // Guid
  restaurantId: number;
  rewardName: string;
  description: string;
  requiredPoints: number;
  rewardValue: number;
  rewardType: RewardType;
  isActive: boolean;
  // Computed by backend for convenience
  isAffordable?: boolean;
  maxRedemptionsPerCustomer?: number;
  validFrom: Date;
  validUntil?: Date;
  termsAndConditions?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface RewardDetailDto extends RewardDto {
  fullDescription?: string;
  expiry?: Date;
  maxRedemptionsPerCustomer?: number;
  currentRedemptions?: number;
  imageUrl?: string;
  metadata?: any;
}

export interface CreateRewardDto {
  restaurantId: number;
  rewardName: string;
  description?: string;
  requiredPoints: number;
  rewardValue?: number;
  rewardType: RewardType;
  validFrom?: Date;
  validUntil?: Date;
  termsAndConditions?: string;
  maxRedemptionsPerCustomer?: number;
  isActive?: boolean;
}

export interface UpdateRewardDto extends Partial<CreateRewardDto> { }

export interface AvailableRewardsResponse {
  customerId: string;
  restaurantId: number;
  availableRewards: RewardDto[];
  currentPoints: number;
}

export interface RewardRedemptionDto {
  redemptionId: string;
  customerId: string;
  rewardId: string;
  rewardName: string;
  pointsUsed: number;
  redemptionDate: Date;
  status: string;
}

// Analytics Types
export interface CustomerAnalyticsDto {
  customerId: string; // Guid
  fullName: string;
  totalVisits: number;
  totalSpent: number;
  pointsEarned: number;
  pointsRedeemed: number;
  currentBalance: number;
  lastVisit: Date;
  averageOrderValue: number;
  daysSinceLastVisit: number;
}

export interface LoyaltyAnalyticsDto {
  restaurantId: number;
  totalCustomers: number;
  activeCustomers: number;
  totalPointsIssued: number;
  totalPointsRedeemed: number;
  currentPointsBalance: number;
  totalRevenueGenerated: number;
  rewardsRedeemed: number;
  averagePointsPerCustomer: number;
  customerRetentionRate: number;
  analyticsDate: Date;
}

export interface PointsActivityDto {
  period: string; // e.g., "Week 1", "2024-01-01"
  pointsEarned: number;
  pointsRedeemed: number;
  netPoints: number;
  transactionCount: number;
}

export interface DailyEngagementDto {
  date: string; // ISO date string
  dayOfWeek: string; // e.g., "Monday"
  visits: number;
  pointsEarned: number;
  newSignups: number;
  revenue: number;
  ordersCount: number;
}

export interface TopPerformingDayDto {
  date: string;
  dayOfWeek: string;
  visits: number;
  revenue: number;
  pointsEarned: number;
  performanceScore: number; // 0-100
}

export interface TimeSeriesAnalyticsDto {
  pointsActivity: PointsActivityDto[];
  dailyEngagement: DailyEngagementDto[];
  topPerformingDays: TopPerformingDayDto[];
  timeRange: string; // e.g., "7days", "30days", "90days"
}

export interface TierProgressResponseDto {
  customerId: string; // Guid
  currentTier: string;
  currentPoints: number;
  nextTier?: string;
  pointsNeededForNextTier?: number;
  progressPercentage: number;
  benefits: string[];
  achievementDate?: Date;
}

// Points Calculation Types
export interface PointsCalculationRequest {
  restaurantId: number;
  orderAmount: number;
}

export interface PointsCalculationResponse {
  orderAmount: number;
  pointsToEarn: number;
  pointsPerRupee: number;
  meetsMinimumOrderAmount: boolean;
  minimumOrderAmount: number;
  expiryDate: Date;
}

// Preview Redemption
export interface PreviewRedemptionRequest {
  restaurantId: number;
  customerId: string;
  rewardId: string;
}

export interface PreviewRedemptionResponse {
  customerId: string;
  rewardId: string;
  canRedeem: boolean;
  requiredPoints: number;
  customerPointsBefore: number;
  customerPointsAfter?: number;
  reasons?: string[]; // If cannot redeem
}

// Notification Types
export enum NotificationType {
  Promotional = 0,
  Reminder = 1,
  PointsAlert = 2,
  RewardExpiry = 3,
  Birthday = 4,
  Welcome = 5,
  Achievement = 6,
  Custom = 7,
}

export enum DeliveryMethod {
  Email = 0,
  SMS = 1,
  Both = 2,
  Push = 3,
}

export interface PointsReminderRequestDto {
  channel: "sms" | "email" | "both" | "whatsapp";
  customMessage?: string;
  filters?: {
    minPoints?: number;
    maxPoints?: number;
    expiryWithinDays?: number;
    validityDurationDays?: number;
    tiers?: string[]; // ['Bronze', 'Silver', 'Gold', 'Platinum']
    lastVisitWithinDays?: number;
  };
}

export interface PointsReminderResponseDto {
  customersTargeted: number;
  messagesQueued: number;
  messagesFailed: number;
  executionTime: string;
  filters: any;
  summary: string;
}

export interface NotificationFilters {
  minPoints?: number;
  maxPoints?: number;
  expiryWithinDays?: number;
  tiers?: string[]; // ['Bronze', 'Silver', 'Gold', 'Platinum']
  lastVisitWithinDays?: number;
  validityDurationDays?: number;
}

export interface TierProgressNotificationRequestDto {
  channel: "sms" | "email" | "both" | "whatsapp";
  customMessage?: string;
  filters?: {
    tiers?: string[]; // Target customers in specific tiers
    progressThreshold?: number; // Minimum progress percentage towards next tier (e.g., 80 for 80%)
    daysToTierUpgrade?: number; // Customers close to tier upgrade
    lastVisitWithinDays?: number;
  };
}

export interface TierProgressNotificationResponseDto {
  customersTargeted: number;
  messagesQueued: number;
  messagesFailed: number;
  executionTime: string;
  filters: any;
  summary: string;
}

export interface PromotionalNotificationRequestDto {
  channel: "sms" | "email" | "both" | "whatsapp";
  customMessage?: string;
  filters?: {
    tiers?: string[]; // Target specific tiers
    minPoints?: number;
    maxPoints?: number;
    lastVisitWithinDays?: number;
    segments?: string[]; // Customer segments
  };
  promotionalOffer?: {
    title: string;
    description: string;
    validUntil?: string;
    discountPercentage?: number;
  };
}

export interface PromotionalNotificationResponseDto {
  customersTargeted: number;
  messagesQueued: number;
  messagesFailed: number;
  executionTime: string;
  filters: any;
  summary: string;
}

// Analytics Types
export interface LoyaltyTimeseriesDataPoint {
  date: string;
  newCustomers: number;
  activeCustomers: number;
  pointsIssued: number;
  pointsRedeemed: number;
  revenueGenerated: number;
  transactionsCount: number;
  rewardsRedeemed: number;
  customerRetentionRate: number;
  averageOrderValue: number;
}

export interface LoyaltyTimeseriesResponse {
  restaurantId: number;
  period: string;
  startDate: string;
  endDate: string;
  dataPoints: LoyaltyTimeseriesDataPoint[];
  summary: {
    totalDataPoints: number;
    totalNewCustomers: number;
    totalPointsIssued: number;
    totalPointsRedeemed: number;
    totalRevenueGenerated: number;
    totalTransactions: number;
    totalRewardsRedeemed: number;
    averageRetentionRate: number;
    averageOrderValue: number;
  };
}

export interface EngagementTrendDataPoint {
  timestamp: string;
  value: number;
  label: string;
  metadata: {
    total_activities: number;
    total_points: number;
    engagement_rate: number;
  };
}

export type EngagementTrendResponse = EngagementTrendDataPoint[];
